import { useRef, useState } from "react";

import { action } from "mobx";
import { observer } from "mobx-react-lite";

import { NodePropertyInput } from "@/components/doc/right-sidebar/node-viewer/NodePropertyInput";
import { getSuggestionNode } from "@/components/suggestions/providers/utils";
import { Suggestions } from "@/components/suggestions/Suggestions";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { VariableNodeModel } from "@/models/nodes/VariableNodeModel";
import { keyMatcher } from "@/utils/keyMatcher";

import type { Suggestion } from "@/components/suggestions/types";
import type { FunctionNodeModel } from "@/models/nodes/FunctionNodeModel";
import type { NodeModel } from "@/models/nodes/types";

type Props = {
  parent: FunctionNodeModel;
  node?: NodeModel;
  onSelect?: (suggestion: Suggestion) => void;
  label?: string;
  autoFocus?: boolean;
};

export const FunctionNodeParameter = observer(function FunctionNodeParameter({
  parent,
  parent: { doc },
  node,
  onSelect = () => {},
  label = "of",
  autoFocus,
}: Props) {
  function getInitialValue() {
    if (node?.is(VariableNodeModel)) {
      return node.value;
    }

    return "";
  }

  const [value, setValue] = useState(getInitialValue);
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  function resetInput() {
    // prevents Jest 'not wrapped in act' warning
    queueMicrotask(() => inputRef.current?.blur());
    setValue(getInitialValue());
  }

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (keyMatcher("Escape", e)) {
      resetInput();
    }
  };

  const handleOnSelect = action((suggestion: Suggestion) => {
    if (suggestion.type !== "single") return;
    const [newNode] = getSuggestionNode(suggestion, doc) ?? [];

    if (newNode?.is(VariableNodeModel)) {
      if (node) {
        node.replaceWith(newNode);
      } else if (parent.paramsAreEmpty) {
        parent.parameters[0].appendChild(newNode);
      } else {
        parent.appendChild(doc.create(InlineNodeModel, [newNode]));
      }

      resetInput();
      onSelect(suggestion);
    }
  });

  const handleFocus = () => {
    setFocused(true);
  };

  const handleBlur = () => {
    setFocused(false);
  };

  return (
    <div className="relative">
      <NodePropertyInput
        label={label}
        autoFocus={autoFocus}
        value={value}
        ref={inputRef}
        onChange={handleOnChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
      {value.length > 0 && focused && (
        <Suggestions doc={doc} value={value} accept={["variable"]} onSelect={handleOnSelect} />
      )}
    </div>
  );
});
